import { PortalProvider } from "@gorhom/portal";
import { useFonts } from "expo-font";
import React, { FC, useCallback, useMemo, useState } from "react";
import { Platform, ScrollView } from "react-native";
import { SafeAreaProvider } from "react-native-safe-area-context";
import { Navigate, Route, Routes } from "react-router";
import { Aurora } from "@lookiero/aurora";
import { EventProvider } from "@lookiero/event";
import { PaymentsBridge, PaymentsQueryProvider, setPaymentsBridge } from "@lookiero/payments-front";
import { SentryEnvironment } from "@lookiero/sty-psp-logging/sentry";
import { Segment } from "@lookiero/sty-psp-segment";
import "@lookiero/sty-psp-ui";
import { DummyLayout, themeByTradename, Text } from "@lookiero/sty-psp-ui";
import { devSettingsForWeb } from "./devSettings";
import { KameleoonEnvironment } from "./infrastructure/ab-testing/kameleoonEnvironment";
import { bootstrap as checkoutBootstrap } from "./infrastructure/delivery/bootstrap";
import { bootstrap as checkoutMockBootstrap } from "./infrastructure/delivery/bootstrap.mock";
import { root } from "./infrastructure/ui/Root";
import { Router } from "./infrastructure/ui/routing/router/Router";
import { Customer } from "./projection/customer/customer";
import { RELEASE } from "./version";

const { tradename, locale, country } = devSettingsForWeb();
const theme = themeByTradename({ tradename });

const customer: Customer = {
  customerId: "e4450481-f8d1-47bd-a2f0-a8cb0d292bcd",
  country,
  segment: Segment.WOMEN,
  email: "email@example.com",
  name: "Adèle Léonce Émilie",
};

const sentryConfig: SentryEnvironment = {
  publicKey: "66cadf9444db4ea5945670f12ec08ae7",
  release: RELEASE,
  project: "4504400729276416",
  environment: "development",
  dist: "web-standalone",
};

const apiUrl =
  Platform.OS !== "web"
    ? "https://web2.sp.dev.aws.lookiero.es/checkout/api"
    : __DEV__
      ? process.env.EXPO_PUBLIC_CI
        ? "/local-to-dev"
        : "http://localhost:3004/local-to-dev"
      : "/checkout/api";

const useRedirect: () => Record<string, string> = () => ({
  returnUrl: "https://web2.dev.aws.lookiero.es/user/",
});

const paymentsBridge: Omit<PaymentsBridge, "getToken"> = {
  stripeKey: "pk_test_6dwdUfkUm7AaUH3oSje9H0kk",
  stripeUkKey:
    "pk_test_51Jw6NEHQ1gSUXaQ8KKBLBhmzw7k0TCTN0GQ6qq8oGRmXCn09ptn7zrHX5jsyidv8iBDGuzUDV3R0fko01m5oJzMO00z6XmoLbn",
  commonAssetsPath: "https://cdn.dev.envs.lookiero.tech/commons",
  paymentsAssetsPath: "https://cdn.dev.envs.lookiero.tech/payments-front",
  useAvoidKeyboard: () => ({
    // @ts-ignore
    setAvoidKeyboard: () => void 0,
  }),
  emitTrackingEvent: (e) => {
    console.log("Tracking Payment event", e);
  },
  appVersion: RELEASE,
  graphqlUri: "/graphql",
  // babel.config.js aliases @lookiero/payments-front to the shared fake (fake-dependencies/) in
  // every Metro build of this app, which is the only place ExpoRoot runs: no real bridge ever reads
  // this, it mirrors what the host passes in production.
  locale: () => locale,
  scrollView: ScrollView,
  hostUrl: "",
  host: "",
  factoringSnippetId: "",
  tradename,
};

// LK dev-site values for standalone `pnpm dev` only — the host injects the real map per deploy.
// Empty like every deployed checkout surface; to exercise the engine locally, temporarily add a
// key holding a real experiment id from the Kameleoon LK dev site.
const kameleoonConfig: KameleoonEnvironment = {
  siteCode: "aplm4v3ckn",
  experiments: {},
};

interface ExpoRootProps {
  readonly getAuthToken: () => Promise<string>;
}

const ExpoRoot: FC<ExpoRootProps> = ({ getAuthToken }) => {
  const [fontsLoaded] = useFonts({
    ["AreaInktrap-Semibold"]: require("@lookiero/aurora/build/assets/fonts/AreaInktrap-Semibold.otf"),
    ["AreaNormal-Semibold"]: require("@lookiero/aurora/build/assets/fonts/AreaNormal-Semibold.otf"),
    ["AreaNormal-Extrabold"]: require("@lookiero/aurora/build/assets/fonts/AreaNormal-Extrabold.otf"),
    ["OpenSans-Regular"]: require("@lookiero/aurora/build/assets/fonts/OpenSans-Regular.ttf"),
    ["OpenSans-Bold"]: require("@lookiero/aurora/build/assets/fonts/OpenSans-Bold.ttf"),
    auroraicons: require("@lookiero/aurora-iconfont/dist/auroraicons.ttf"),
  });

  const [isAccessible, setIsAccessible] = useState<boolean>();
  const onNotAccessible = useCallback(() => setIsAccessible(false), []);

  const Root = useMemo(() => {
    setPaymentsBridge({ ...paymentsBridge, getToken: getAuthToken });

    const { Component: Messaging, queryBus } =
      process.env.EXPO_PUBLIC_APP_VARIANT === "test"
        ? checkoutMockBootstrap()
        : checkoutBootstrap({ apiUrl: () => apiUrl, getAuthToken });

    return root({
      Messaging,
      queryBus,
      getAuthToken,
      development: false,
      sentry: () => (process.env.EXPO_PUBLIC_APP_VARIANT === "test" ? ({} as SentryEnvironment) : sentryConfig),
      kameleoon: () => kameleoonConfig,
    })({ customerId: customer.customerId });
  }, [getAuthToken]);

  return fontsLoaded ? (
    <SafeAreaProvider>
      <PortalProvider>
        <PaymentsQueryProvider>
          <EventProvider>
            <Aurora theme={theme}>
              {isAccessible === false && (
                <Text testID="checkout-not-accessible" variant="heading">
                  Checkout is not accessible!
                </Text>
              )}

              <Router>
                <Routes>
                  <Route
                    path="/checkout/*"
                    element={
                      <Root
                        basePath="/checkout"
                        customer={customer}
                        layout={DummyLayout}
                        locale={locale}
                        tradename={tradename}
                        useRedirect={useRedirect}
                        onCheckoutFlowSuccess={() => console.log("Checkout flow success!")}
                        onNotAccessible={onNotAccessible}
                      />
                    }
                  />

                  <Route element={<Navigate to="/checkout" replace />} path="*" />
                </Routes>
              </Router>
            </Aurora>
          </EventProvider>
        </PaymentsQueryProvider>
      </PortalProvider>
    </SafeAreaProvider>
  ) : null;
};

export { ExpoRoot };
